import { useCallback, useEffect, useState } from "react";
import { Loader2, Mail, Send, CheckCircle2, Monitor, Smartphone } from "lucide-react";
import { api, errText } from "../../lib/api";

const LOCALES = [["en", "English"], ["fr", "Français"], ["es", "Español"], ["it", "Italiano"], ["nl", "Nederlands"], ["de", "Deutsch"], ["ar", "العربية"]];

export default function EmailTemplatesPage() {
  const [meta, setMeta] = useState(null);
  const [active, setActive] = useState("order_confirmation");
  const [locale, setLocale] = useState("en");
  const [html, setHtml] = useState("");
  const [subject, setSubject] = useState("");
  const [narrow, setNarrow] = useState(false);
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/admin/email-templates").then((r) => setMeta(r.data)).catch((e) => setErr(errText(e)));
  }, []);

  const loadPreview = useCallback(async () => {
    setBusy("preview"); setErr("");
    try {
      const { data } = await api.get(`/admin/email-templates/${active}/preview`, { params: { locale } });
      setHtml(data);
      setSubject((String(data).match(/<!-- subject: (.*?) -->/) || [])[1] || "");
    } catch (e) { setErr(errText(e)); }
    finally { setBusy(""); }
  }, [active, locale]);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const sendTest = async () => {
    setBusy("send"); setMsg(""); setErr("");
    try {
      const { data } = await api.post(`/admin/email-templates/${active}/test`, { to, locale });
      if (data.ok) setMsg(`Sent to ${data.to}.`);
      else setErr(data.reason === "not_configured"
        ? "No Resend API key is saved yet — add it in Platform settings → Email, SMS & WhatsApp."
        : `The e-mail could not be sent: ${data.reason}`);
    } catch (e) { setErr(errText(e)); }
    finally { setBusy(""); }
  };

  if (!meta) return <p className="text-sm text-muted" data-testid="email-templates-loading">Loading…</p>;
  const f = meta.footer || {};

  return (
    <div className="space-y-5" data-testid="admin-email-templates-page">
      <div>
        <h1 className="font-display text-3xl text-ink">E-mail templates</h1>
        <p className="text-sm text-muted max-w-2xl">
          Every transactional e-mail the platform sends, in all four languages. The logo, support details and legal
          footer below are pulled live from Platform settings — change them there and every e-mail follows.
        </p>
      </div>

      {msg ? <p className="text-sm text-success inline-flex items-center gap-1.5" data-testid="email-templates-message">
        <CheckCircle2 size={14} /> {msg}</p> : null}
      {err ? <p className="text-sm text-danger" data-testid="email-templates-error">{err}</p> : null}

      <div className="card p-4 grid gap-3 md:grid-cols-3 text-xs" data-testid="email-footer-facts">
        <div><span className="smallcaps block mb-1">Support</span>
          <span className="text-ink">{f.support_email || "—"}</span><br />
          <span className="text-muted">{f.support_whatsapp || "no WhatsApp set"} · {f.support_hours || "no hours set"}</span>
        </div>
        <div><span className="smallcaps block mb-1">Legal footer</span>
          <span className="text-ink">{f.legal_name || "—"}</span><br />
          <span className="text-muted">{f.legal_address || "no address set"}{f.vat_id ? ` · VAT ${f.vat_id}` : ""}</span>
        </div>
        <div><span className="smallcaps block mb-1">Review link</span>
          <span className="text-muted break-all">{meta.review_url}</span>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[260px_1fr]">
        <div className="card p-2 h-fit" data-testid="email-template-list">
          {(meta.templates || []).map((t) => (
            <button key={t.key} onClick={() => setActive(t.key)} data-testid={`email-template-${t.key}`}
                    className={`w-full text-start px-3 py-2 text-[12px] transition-colors duration-150 ${
                      active === t.key ? "bg-ink text-plaster" : "text-ink hover:bg-plaster"}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="space-y-3">
          <div className="card p-3 flex flex-wrap items-center gap-2">
            <select className="field w-auto" value={locale} data-testid="email-template-locale"
                    onChange={(e) => setLocale(e.target.value)}>
              {LOCALES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <button className="btn-ghost text-[11px] inline-flex items-center gap-1.5" data-testid="email-template-width"
                    onClick={() => setNarrow((n) => !n)}>
              {narrow ? <Smartphone size={13} /> : <Monitor size={13} />} {narrow ? "Mobile" : "Desktop"}
            </button>
            <div className="flex-1" />
            <input className="field w-auto min-w-[200px]" type="email" placeholder="you@example.com"
                   value={to} data-testid="email-template-to" onChange={(e) => setTo(e.target.value)} />
            <button className="btn-primary text-[11px] px-4 inline-flex items-center gap-2" disabled={!to || busy}
                    onClick={sendTest} data-testid="email-template-send">
              {busy === "send" ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send test
            </button>
          </div>

          <p className="text-xs text-muted inline-flex items-center gap-1.5" data-testid="email-template-subject">
            <Mail size={13} /> <b className="text-ink">Subject:</b> {subject || "—"}
          </p>

          <div className="card p-0 overflow-hidden bg-[#F3EDE6]">
            {busy === "preview" ? (
              <p className="p-6 text-sm text-muted">Rendering…</p>
            ) : (
              <iframe title="E-mail preview" srcDoc={html} data-testid="email-template-preview"
                      style={{ width: narrow ? 390 : "100%", height: 760, border: 0, display: "block",
                               margin: narrow ? "0 auto" : 0 }} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
